import {useCallback, useEffect, useMemo, useState} from 'react';

import {SmsSidebarHeader} from 'shells/inbox/sidebar/sms-sidebar-header';
import type {UpdateAction} from 'shells/inbox/types';
import {Sidebar} from 'shells/sidebar/sidebar';
import {UserSelectButton} from 'shells/user-select-button/user-select-button';

import {useGetUsers} from '../../../core/select/hooks';
import {useGetSessionUser} from '../../../core/use-get-session-user';
import {OpenableModals, useOpenableModal} from '../../../openable-modals';

import {appHistory} from '../../../../history';
import {useDismissibleUserSetting} from '../../../../util/hooks/use-dismissible-user-setting';

import {
    useGetChannelEvents,
    useUpdateThreadStatus,
    useReassignThread,
    useGetInboxThreadById,
} from '../../graphql/hooks';
import {getThreadStatusFromEffectiveStatus, getCanThreadBeReassigned} from '../../helpers';
import {useGetRelatedTextables} from '../../sms/graphql/hooks';

import {SidebarContentWrapper} from './sidebar-content-wrapper';
import {SmsOnboarding} from './sms-onboarding';

const HAS_VIEWED_TEXT_MESSAGE_ONBOARDING = 'has_viewed_text_message_onboarding';

type Props = {
    entityId: string;
    openedFromEntityName: string | null | undefined;
    openedFromPhoneNumber: string | null | undefined;
    channelId: string | null | undefined;
    threadId: string | null | undefined;
};

export function SmsSidebarWrapper(props: Props) {
    const {channelId, threadId, openedFromEntityName, openedFromPhoneNumber, entityId} = props;
    const [selectedTextable, setSelectedTextable] = useState<any | null | undefined>(null);
    const [selectedChannel, setSelectedChannel] = useState<any | null | undefined>(null);
    const [selectedThreadId, setSelectedThreadId] = useState<string | null | undefined>(threadId);
    const [selectedTextableDidUpdate, setSelectedTextableDidUpdate] = useState(false);
    const [isStartingNewThread, setIsStartingNewThread] = useState(false);
    // Set to true until 500ms after textables are loaded to allow time for auto-selecting a textable
    const [isAutoSelectingTextable, setIsAutoSelectingTextable] = useState(true);
    const {textables, isLoading: isLoadingTextables, refetch} = useGetRelatedTextables(entityId);
    const {onClose} = useOpenableModal(OpenableModals.SMS_SIDEBAR);
    const {thread: selectedThread} = useGetInboxThreadById(selectedThreadId);
    const {user} = useGetSessionUser();
    const {users} = useGetUsers();
    const {updateThreadStatus} = useUpdateThreadStatus();
    const {reassignThread} = useReassignThread();
    const {
        channelEvents,
        isLoading: isLoadingChannelEvents,
        fetchMostRecentChannelEvents,
        refetchScheduledChannelEvents,
        hasNextPage,
        fetchMoreRows,
    } = useGetChannelEvents(selectedChannel && selectedChannel.id, selectedThreadId);

    const agents = useMemo(() => {
        return users.filter((userModel) => userModel.isAgent);
    }, [users]);

    useEffect(() => {
        if (openedFromPhoneNumber && textables && textables.length && !selectedTextable) {
            // auto-select the textable matching the openedFromPhoneNumber if there is one
            const matchingTextable = textables.find(
                (textable) => textable.id === `${entityId}${openedFromPhoneNumber}`
            );

            if (matchingTextable) {
                setSelectedTextable(matchingTextable);
            }
        } else if (textables && channelId && !selectedTextable) {
            // auto-select the textable matching the channelId from props if there is one
            const matchingTextable = textables.find(
                (textable) =>
                    textable.phone.value.channel && textable.phone.value.channel.id === channelId
            );
            if (matchingTextable) {
                setSelectedTextable(matchingTextable);
            }
        } else if (textables && textables.length === 1 && !selectedTextable) {
            // auto-select the first textable if there's only one and set the selected channel for fetching the thread
            const firstTextable = textables[0];
            setSelectedTextable(firstTextable);
        } else if (selectedTextable) {
            const newChannel =
                selectedTextable.phone &&
                selectedTextable.phone.value &&
                selectedTextable.phone.value.channel
                    ? selectedTextable.phone.value.channel
                    : null;
            const latestThread = newChannel && newChannel.threads.latestThread;

            setSelectedChannel(newChannel);
            setSelectedThreadId(latestThread && latestThread.id);
        }
    }, [textables, selectedTextable, channelId, openedFromPhoneNumber, entityId]);

    // isAutoSelectingTextable is set to true until 500ms after textables are loaded to allow time for auto-selecting a textable
    useEffect(() => {
        if (!isLoadingTextables && textables && !selectedTextable) {
            setTimeout(() => {
                setIsAutoSelectingTextable(false);
            }, 500);
        }
    }, [isLoadingTextables, textables, selectedTextable]);

    // if the textable is updated, update the textable state to the new version
    useEffect(() => {
        if (textables && textables.length && selectedTextable && selectedTextableDidUpdate) {
            const updatedTextable = textables.find(
                (textable) => textable.id === selectedTextable.id
            );
            setSelectedTextable(updatedTextable);
            setSelectedTextableDidUpdate(false);
        }
    }, [textables, selectedTextable, selectedTextableDidUpdate]);

    const latestThreadOnSelectedChannel = selectedChannel && selectedChannel.threads.latestThread;
    // Keep the selected thread id in sync when a new channel is created
    useEffect(() => {
        setSelectedThreadId(latestThreadOnSelectedChannel && latestThreadOnSelectedChannel.id);
    }, [latestThreadOnSelectedChannel]);

    const selectedThreadStatus = selectedThread && selectedThread.effectiveStatus;
    const selectedThreadOwnerId = selectedThread && selectedThread.owner && selectedThread.owner.id;
    const userCanReassign = user && user.isAdministrator;

    const {shouldShow: shouldShowOnboarding, onDismiss} = useDismissibleUserSetting(
        HAS_VIEWED_TEXT_MESSAGE_ONBOARDING
    );

    const handleClose = useCallback(() => {
        Timeline.refreshTimeline();

        if (onDismiss) {
            onDismiss().then(onClose);
        } else {
            onClose();
        }
    }, [onClose, onDismiss]);

    // Close modal when route changes
    useEffect(() => {
        const unlisten = appHistory.listen(() => {
            handleClose();
        });

        return unlisten;
    }, [handleClose]);

    return (
        <Sidebar
            isOpen={true}
            preventBodyScroll={true}
            canClickOutsideToClose={true}
            canEscapeToClose={true}
            onClose={handleClose}
        >
            {shouldShowOnboarding ? (
                <SmsOnboarding onClose={handleClose} />
            ) : (
                <>
                    <SmsSidebarHeader
                        isLoading={isLoadingTextables}
                        openedFromEntityId={entityId}
                        selectedTextable={selectedTextable}
                        onSelectTextable={setSelectedTextable}
                        textables={textables}
                        // @ts-expect-error
                        onUpdateStatus={
                            selectedThreadId && !isStartingNewThread
                                ? (status: UpdateAction) => {
                                      return updateThreadStatus(selectedThreadId, status).then(
                                          () => {
                                              refetch().then(() => {
                                                  setSelectedTextableDidUpdate(true);
                                                  fetchMostRecentChannelEvents();
                                              });
                                          }
                                      );
                                  }
                                : undefined
                        }
                        status={getThreadStatusFromEffectiveStatus(selectedThreadStatus)}
                        assigneeButton={
                            selectedThreadId && !isStartingNewThread ? (
                                <UserSelectButton
                                    isSmall={true}
                                    isReadOnly={
                                        !userCanReassign ||
                                        Boolean(
                                            latestThreadOnSelectedChannel &&
                                                !getCanThreadBeReassigned(
                                                    latestThreadOnSelectedChannel
                                                )
                                        )
                                    }
                                    selectedUserId={selectedThreadOwnerId}
                                    onSelect={(assigneeId?: string | null) => {
                                        const selectedUser = users.find((u) => u.id === assigneeId);

                                        if (selectedUser) {
                                            reassignThread({
                                                threadId: selectedThreadId,
                                                assigneeId: selectedUser.id,
                                                assigneeName: selectedUser.name,
                                                assigneeAvatarUrl: selectedUser.avatarUrl,
                                                assigneeInitials: selectedUser.initials,
                                            }).then(() => {
                                                refetch().then(() => {
                                                    setSelectedTextableDidUpdate(true);
                                                });
                                            });
                                        }
                                    }}
                                    users={agents}
                                />
                            ) : undefined
                        }
                    />
                    <SidebarContentWrapper
                        isLoadingTextables={isLoadingTextables}
                        isAutoSelectingTextable={isAutoSelectingTextable}
                        selectedTextable={selectedTextable}
                        selectedChannel={selectedChannel}
                        setSelectedChannel={setSelectedChannel}
                        selectedThread={selectedThread}
                        textables={textables}
                        includeLeadIds={entityId.split('-')[1] === 'leads'}
                        entityId={entityId}
                        openedFromEntityName={openedFromEntityName}
                        onCloseSidebar={onClose}
                        openThreadIdOnSelectedChannel={
                            latestThreadOnSelectedChannel && latestThreadOnSelectedChannel.isOpen
                                ? latestThreadOnSelectedChannel.id
                                : undefined
                        }
                        setSelectedThreadId={setSelectedThreadId}
                        isStartingNewThread={isStartingNewThread}
                        setIsStartingNewThread={setIsStartingNewThread}
                        setSelectedTextableDidUpdate={setSelectedTextableDidUpdate}
                        refetchTextables={refetch}
                        channelEvents={channelEvents}
                        isLoadingChannelEvents={isLoadingChannelEvents}
                        fetchMostRecentChannelEvents={fetchMostRecentChannelEvents}
                        refetchScheduledChannelEvents={refetchScheduledChannelEvents}
                        hasNextPage={hasNextPage}
                        fetchMoreRows={fetchMoreRows}
                    />
                </>
            )}
        </Sidebar>
    );
}
